import { useMemo } from 'react';
import { Eyebrow, LinkButton, Pill } from '@/components/ui';
import { cn } from '@/lib/format';
import { buildCoachPreview } from '@/lib/coachDemo';
import { AlertIcon, ArrowRightIcon, LockIcon, ShieldIcon, SparkIcon, TrendUpIcon } from '@/lib/icons';

const VERDICT_TONE: Record<string, string> = {
  on_track: 'border-success/50 text-success',
  progressing_fast: 'border-success/50 text-success',
  stalled: 'border-warning/50 text-warning',
  overreaching: 'border-warning/50 text-warning',
  deload_recommended: 'border-accent/60 text-accent',
  insufficient_data: 'border-hairline text-ink-muted',
};

/**
 * Section 7: the AI coach report preview. It renders the live output of
 * packages/coach-engine running over the seeded 4-week evidence packet.
 */
export function CoachPreview() {
  const model = useMemo(buildCoachPreview, []);

  return (
    <section id="coach" className="border-b border-hairline bg-page-subtle py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4">
          <Eyebrow>
            <SparkIcon className="mr-1 inline text-sm" /> Premium · AI Performance Coach
          </Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] md:text-display-2">
            Your log has an AI coach in it.
          </h2>
          <p className="max-w-[74ch] text-body-lg text-ink-muted">
            Every Monday the AI coach gathers four weeks of your sets, runs a deterministic evaluation matrix, reasons over
            the result with a bounded ReAct loop, and writes next week — then shows you its reasoning and the safety
            rails it applied. Below is the genuine engine output for a seeded four-week block: twelve sessions, 139
            logged sets, ten personal records.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
          {/* ---------------- the report ---------------- */}
          <div className="min-w-0 rounded-card border border-hairline bg-surface p-6 shadow-card">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-2 rounded-pill border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] ${VERDICT_TONE[model.verdict] ?? 'border-hairline text-ink-muted'}`}>
                  {model.verdict === 'deload_recommended' || model.verdict === 'overreaching' ? (
                    <AlertIcon className="text-sm" />
                  ) : (
                    <TrendUpIcon className="text-sm" />
                  )}
                  {model.verdictLabel}
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                  Week 4 review · {model.sessionsAnalyzed} sessions · {model.prCount} PRs
                </span>
              </div>
              <Pill className="border-accent/50 text-accent">
                <LockIcon className="text-xs" /> Premium
              </Pill>
            </div>

            <p className="mb-7 max-w-[62ch] font-display text-[20px] font-black uppercase leading-tight tracking-[-0.01em] text-ink">
              {model.headline}
            </p>

            <dl className="mb-7 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
              {model.metrics.map((metric) => (
                <div key={metric.label} className="flex flex-col gap-1">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">{metric.label}</dt>
                  <dd className="l2f-tabular font-mono text-xl font-bold leading-none text-ink">{metric.value}</dd>
                  {metric.hint ? <span className="text-[11px] text-ink-muted">{metric.hint}</span> : null}
                </div>
              ))}
            </dl>

            {model.changes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] border-separate border-spacing-0 text-left">
                  <caption className="sr-only">Prescribed changes for next week</caption>
                  <thead>
                    <tr>
                      {['Exercise', 'Next week', 'Rule', 'Why'].map((heading) => (
                        <th
                          key={heading}
                          scope="col"
                          className="border-b border-hairline pb-2 pr-4 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="align-top text-[13px]">
                    {model.changes.slice(0, 6).map((change, index) => (
                      <tr key={`${change.exercise}-${index}`}>
                        <th scope="row" className="border-b border-hairline py-3 pr-4 text-left font-bold text-ink">
                          {change.exercise}
                        </th>
                        <td className="border-b border-hairline py-3 pr-4">
                          <span
                            className={cn(
                              'l2f-tabular inline-flex whitespace-nowrap rounded-pill border px-2 py-[2px] font-mono text-[11px] font-bold',
                              change.action === 'increase_load'
                                ? 'border-accent bg-accent/10 text-accent'
                                : change.action === 'deload' || change.action === 'reduce_load'
                                  ? 'border-warning bg-warning/10 text-warning'
                                  : change.action === 'blocked'
                                    ? 'border-danger bg-danger/10 text-danger'
                                    : 'border-hairline text-ink',
                            )}
                          >
                            {change.change}
                          </span>
                        </td>
                        <td className="border-b border-hairline py-3 pr-4 font-mono text-[11px] text-ink-muted">
                          {change.rule}
                        </td>
                        <td className="border-b border-hairline py-3 text-[12px] leading-snug text-ink-muted">
                          {change.rationale}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="rounded-card border border-hairline bg-page-subtle p-4 text-[13px] text-ink-muted">
                No prescription change is needed from this block — the current loads stand.
              </p>
            )}

            {model.markdown ? (
              <details className="mt-6 border-t border-hairline pt-4">
                <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-[0.08em] text-accent">
                  Read the full report your AI coach writes to you
                </summary>
                <pre className="mt-4 max-h-[420px] overflow-auto whitespace-pre-wrap rounded-card border border-hairline bg-page-subtle p-4 font-mono text-[11px] leading-relaxed text-ink">
{model.markdown}
                </pre>
              </details>
            ) : null}
          </div>

          {/* ---------------- reasoning + rails + CTA ---------------- */}
          <div className="flex min-w-0 flex-col gap-5">
            <div className="rounded-card border border-hairline bg-surface p-6 shadow-card">
              <h3 className="mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.08em] text-ink">
                <AlertIcon className="text-sm text-accent" /> What the matrix flagged
              </h3>
              {model.flags.length > 0 ? (
                <ul className="flex flex-col gap-2.5">
                  {model.flags.slice(0, 5).map((flag, index) => (
                    <li key={`${flag}-${index}`} className="flex items-start gap-2 text-[13px] leading-snug text-ink">
                      <span aria-hidden="true" className="mt-[6px] h-1.5 w-1.5 shrink-0 bg-accent" />
                      {flag}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-ink-muted">
                  No fatigue indicators crossed their thresholds in this window — the block is recovering as well as it
                  is loading.
                </p>
              )}

              <h3 className="mb-4 mt-7 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.08em] text-ink">
                <ShieldIcon className="text-sm text-accent" /> Safety rails applied
              </h3>
              {model.railsHit.length > 0 ? (
                <ul className="flex flex-col gap-2.5">
                  {model.railsHit.map((rail, index) => (
                    <li key={`${rail}-${index}`} className="text-[13px] leading-snug text-ink">
                      {rail}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-ink-muted">
                  No prescription in this block breached a rail: every change stayed inside ±10% load and ±30% volume,
                  and no injured movement was loaded. The rails run after the model, not instead of it.
                </p>
              )}
            </div>

            <div className="rounded-card border border-accent/40 bg-accent/10 p-6">
              <h3 className="mb-2 font-display text-[18px] font-black uppercase tracking-[-0.01em] text-accent">
                Unlock the AI coach
              </h3>
              <p className="mb-5 text-[13px] leading-relaxed text-ink">
                Premium adds the weekly review, the prescribed next week, on-demand check-ins mid-block, and the full
                reasoning trail behind every change. Cancel whenever — your log stays yours, exportable as CSV or JSON.
              </p>
              <LinkButton href="#pricing" size="md" className="w-full">
                See Premium pricing <ArrowRightIcon className="text-sm" />
              </LinkButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
